<template>
  <section class="example-gallery" aria-label="Before and after colorization examples">
    <div ref="viewport" class="viewport">
      <div class="track">
        <div
          v-for="(item, idx) in loopStrip"
          :key="`${item.id}-${idx}`"
          class="cell"
          :class="{ 'cell--center': idx === centerIndex }"
        >
          <div class="cell-inner">
            <img
              class="img img-bw"
              :src="item.bw"
              alt=""
              width="400"
              height="500"
              loading="eager"
              decoding="async"
            >
            <img
              class="img img-color"
              :src="item.color"
              alt=""
              width="400"
              height="500"
              loading="eager"
              decoding="async"
            >
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import bw1 from '@/assets/examples/1-bw.webp'
import color1 from '@/assets/examples/1-color.webp'
import bw2 from '@/assets/examples/2-bw.webp'
import color2 from '@/assets/examples/2-color.webp'
import bw3 from '@/assets/examples/3-bw.webp'
import color3 from '@/assets/examples/3-color.webp'
import bw4 from '@/assets/examples/4-bw.webp'
import color4 from '@/assets/examples/4-color.webp'
import bw5 from '@/assets/examples/5-bw.webp'
import color5 from '@/assets/examples/5-color.webp'

const PAIRS = [
  { id: 1, bw: bw1, color: color1 },
  { id: 2, bw: bw2, color: color2 },
  { id: 3, bw: bw3, color: color3 },
  { id: 4, bw: bw4, color: color4 },
  { id: 5, bw: bw5, color: color5 },
]

/** One seamless loop half: several full passes so motion feels dense; duplicated for -50% marquee. */
const LOOP_HALF = [...PAIRS, ...PAIRS, ...PAIRS]

export default {
  name: 'ExampleGallery',
  data() {
    return {
      centerIndex: -1,
      centerRafId: 0,
    }
  },
  computed: {
    loopStrip() {
      return [...LOOP_HALF, ...LOOP_HALF]
    },
  },
  mounted() {
    const tick = () => {
      this.updateCenterHighlight()
      this.centerRafId = requestAnimationFrame(tick)
    }
    this.centerRafId = requestAnimationFrame(tick)
  },
  beforeUnmount() {
    cancelAnimationFrame(this.centerRafId)
  },
  methods: {
    updateCenterHighlight() {
      const root = this.$refs.viewport
      if (!root) return
      const vr = root.getBoundingClientRect()
      const midX = vr.left + vr.width / 2
      let bestIdx = -1
      let bestDist = Infinity
      const cells = root.querySelectorAll('.cell')
      cells.forEach((cell, idx) => {
        const cr = cell.getBoundingClientRect()
        if (cr.right < vr.left || cr.left > vr.right) return
        const cx = cr.left + cr.width / 2
        const dist = Math.abs(cx - midX)
        if (dist < bestDist) {
          bestDist = dist
          bestIdx = idx
        }
      })
      if (bestIdx !== this.centerIndex) {
        this.centerIndex = bestIdx
      }
    },
  },
}
</script>

<style scoped>
.example-gallery {
  /* vw includes scrollbar gutter on some browsers; cqi keys off this strip’s real width (matches local vs live). */
  --skew: -11deg;
  --marquee-duration: 50s;
  --gallery-h: clamp(200px, 28vw, 320px);
  --cell-w: clamp(200px, 22vw, 280px);
  --overlap: clamp(36px, 5vw, 52px);

  container-type: inline-size;
  container-name: example-gallery;
  width: 100%;
  margin: 0;
  padding: 28px 0;
  background: linear-gradient(
    180deg,
    rgba(8, 10, 18, 0.98) 0%,
    rgba(14, 16, 28, 1) 50%,
    rgba(8, 10, 18, 0.98) 100%
  );
}

@supports (width: 1cqi) {
  .example-gallery {
    --gallery-h: clamp(200px, 28cqi, 320px);
    --cell-w: clamp(200px, 22cqi, 280px);
    --overlap: clamp(36px, 5cqi, 52px);
  }
}

.viewport {
  overflow: hidden;
  width: 100%;
  -webkit-mask-image: linear-gradient(
    90deg,
    transparent 0%,
    #000 2%,
    #000 98%,
    transparent 100%
  );
  mask-image: linear-gradient(
    90deg,
    transparent 0%,
    #000 2%,
    #000 98%,
    transparent 100%
  );
}

.track {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: max-content;
  animation: gallery-marquee var(--marquee-duration) linear infinite;
  will-change: transform;
}

.example-gallery:hover .track {
  animation-play-state: paused;
}

@keyframes gallery-marquee {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}

.cell {
  position: relative;
  box-sizing: border-box;
  flex: 0 0 var(--cell-w);
  width: var(--cell-w);
  min-width: var(--cell-w);
  max-width: var(--cell-w);
  height: var(--gallery-h);
  min-height: var(--gallery-h);
  max-height: var(--gallery-h);
  transform: skewX(var(--skew));
  overflow: hidden;
  margin: 0 calc(var(--overlap) / -2);
  z-index: 1;
  transition: z-index 0s;
  cursor: pointer;
  /* If anti-alias / rounding leaves hairline gaps after skew, avoid bright flashes (live WebKit). */
  background: #0b0d14;
  isolation: isolate;
}

.cell--center {
  z-index: 2;
}

.cell:hover {
  z-index: 5;
}

/* Oversized + centered so photos fully cover the skewed clip (155% was too tight on some browsers). */
.cell-inner {
  position: absolute;
  width: 220%;
  height: 128%;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0) skewX(calc(-1 * var(--skew)));
  backface-visibility: hidden;
}

.img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  display: block;
  pointer-events: none;
  backface-visibility: hidden;
  filter: contrast(1.05) brightness(0.98);
}

.img-color {
  opacity: 0;
  transition: opacity 0.55s ease;
}

.cell--center .img-color,
.cell:hover .img-color {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .track {
    animation: none;
  }

  .example-gallery:hover .track {
    animation-play-state: running;
  }

  .img-color {
    opacity: 0;
  }

  .cell--center .img-color,
  .cell:hover .img-color,
  .cell:focus-within .img-color {
    opacity: 1;
  }
}
</style>
